import React from 'react';
import { useInViewAnimation } from '../hooks/useInViewAnimation';

const steps = [
  { n: '01', title: 'Discovery',        desc: 'Understand the business, users and the risk profile before writing any code.' },
  { n: '02', title: 'Business Analysis', desc: 'Translate goals into measurable outcomes, KPIs and a shaped scope.' },
  { n: '03', title: 'UI/UX',              desc: 'Prototype the experience &mdash; validated with real users, not opinions.' },
  { n: '04', title: 'Architecture',       desc: 'Cloud-native, secure and scalable system design tuned to your constraints.' },
  { n: '05', title: 'Development',        desc: 'Weekly increments in a live environment. You use it as it grows.' },
  { n: '06', title: 'Quality Assurance',  desc: 'Automated + manual QA, security scans, performance testing before every ship.' },
  { n: '07', title: 'Deployment',         desc: 'Zero-downtime rollouts on AWS, Azure or GCP with observability wired in.' },
  { n: '08', title: 'Maintenance',        desc: 'SLAs, monitoring and continuous improvement long after go-live.' },
];

export function ProcessSection() {
  const { ref, isInView } = useInViewAnimation();

  return (
    <section
      ref={ref}
      data-testid="process-section"
      className="max-w-[1200px] mx-auto px-6 py-16 md:py-20"
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10 md:mb-14">
        <h2
          className={`text-[32px] md:text-[40px] lg:text-[44px] leading-[1.1] text-[#0D212C] tracking-tight ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.05s' }}
        >
          Our <span className="font-serif grad-text">Development Process</span>
        </h2>
        <p
          className={`text-sm md:text-base text-[#273C46] max-w-md ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.15s' }}
        >
          A boring-in-a-good-way eight-phase process that consistently delivers enterprise-grade outcomes.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {steps.map((s, i) => (
          <div
            key={s.n}
            data-testid={`process-step-${s.n}`}
            className={`relative rounded-[28px] bg-white border border-[#7C3AED]/10 p-6 md:p-7 shadow-[0_4px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_40px_rgba(109,40,217,0.18)] hover:border-[#7C3AED]/40 hover:-translate-y-1 transition-all duration-300 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: `${0.08 + i * 0.06}s` }}
          >
            <div className="font-mono text-xs text-[#7C3AED] mb-3">[{s.n}]</div>
            <h3 className="font-serif text-2xl text-[#0D212C] mb-2">{s.title}</h3>
            <p
              className="text-sm text-[#273C46] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: s.desc }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
